import {
  Button,
  HStack,
  Icon,
  Spinner,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FiDownload, FiFileText } from "react-icons/fi";
import { useState } from "react";
import { ApiAuth, ApiError } from "../../../lib/api";
import { formatDateSpelled } from "../../../utils/appLocale";
import { billingApi } from "./api";
import { useItemDocuments } from "./hooks";
import { BillingDocument } from "./types";

type Props = {
  auth: ApiAuth;
  itemId: string;
  enabled: boolean;
};

const KIND_LABEL: Record<string, string> = {
  invoice: "Invoice",
  receipt: "Receipt",
  credit_note: "Credit note",
};

// The app's language, not the browser's — see `formatDate` in ./format.
// An invoice dated in a month name the rest of the page does not speak is the
// same defect, on the document a patient is most likely to keep.
const formatIssuedAt = (epoch: number) => formatDateSpelled(epoch);

export const DocumentsList = ({ auth, itemId, enabled }: Props) => {
  const docs = useItemDocuments(auth, itemId, enabled);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const toast = useToast();

  const onDownload = async (doc: BillingDocument) => {
    setDownloadingId(doc.documentId);
    try {
      const res = await billingApi.documentDownload(auth, doc.documentId);
      window.open(res.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      const msg = (err as ApiError)?.message || "Could not download the file";
      toast({ title: "Download failed", description: msg, status: "error" });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!enabled) return null;

  if (docs.isLoading) {
    return (
      <HStack spacing={2} mt={2} color="#6b7280" fontSize="13px">
        <Spinner size="xs" />
        <Text>Loading documents…</Text>
      </HStack>
    );
  }
  if (docs.error) {
    return (
      <Text fontSize="13px" color="#b42318" mt={2}>
        {(docs.error as ApiError)?.message || "Could not load documents."}
      </Text>
    );
  }
  const list = docs.data?.documents ?? [];
  if (list.length === 0) {
    return (
      <Text fontSize="13px" color="#6b7280" mt={2}>
        No invoices or receipts available yet.
      </Text>
    );
  }
  return (
    <Stack spacing={2} mt={2}>
      {list.map((doc) => (
        <HStack
          key={doc.documentId}
          spacing={3}
          px={3}
          py={2}
          borderWidth="1px"
          borderColor="#f0f0f0"
          borderRadius="8px"
          bg="#fafafa"
        >
          <Icon as={FiFileText} color="#6b7280" />
          <Text fontSize="13px" color="#1a1f36" flex={1} noOfLines={1}>
            {KIND_LABEL[doc.kind] || doc.kind}
            {doc.issuedAt > 0 && (
              <Text as="span" color="#6b7280" ml={2} fontSize="12px">
                · {formatIssuedAt(doc.issuedAt)}
              </Text>
            )}
          </Text>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<FiDownload />}
            onClick={() => onDownload(doc)}
            isLoading={downloadingId === doc.documentId}
            isDisabled={!doc.hasFile}
            borderColor="#e6e6e6"
            color="#1a1f36"
          >
            Download
          </Button>
        </HStack>
      ))}
    </Stack>
  );
};
